<template>
  <div class="position-relative p-3 m-auto">
    <div ref="threeContainer" class="m-auto" style="width: 90%; height: 500px;"></div>
    <div class="snapshots">
      <img v-for="(snapshot, idx) in snapshots" :key="idx" :src="snapshot" :alt="'Snapshot ' + (idx + 1)" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount,watch  } from 'vue';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { TextureLoader } from 'three/src/loaders/TextureLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export default {
  name: 'ThreeObjComponent',
  props: {
    meshFileURL: {
      type: String,
      required: true,
    },
    textureFileURL: {
      type: String,
      required: true,
    },
    points: {
      type: Array,
      default: () => [],
    },
    viewRightEye: {
      type: Object,
      required: true
    },
    viewLeftEye: {
      type: Object,
      required: true
    }
  },
  emits: ['snapshotsCaptured'],  

  setup(props, { emit }) {
    const threeContainer = ref(null);
    const context = ref(null);
    const pointObjects = ref([]); 
    const snapshots = ref([]); 

    const loadOBJ = (url, textureURL) => {
      console.log('Loading OBJ file:', url);
      console.log('Loading Texture file:', textureURL);

      if (context.value && context.value.scene) {
        while (context.value.scene.children.length > 0) {
          const object = context.value.scene.children[0];
          if (object) {
            object.geometry?.dispose();
            object.material?.dispose();
            context.value.scene.remove(object);
          }
        }
      }

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xffffff);
      context.value = { scene };

      const camera = new THREE.PerspectiveCamera(
        40,
        threeContainer.value.clientWidth / threeContainer.value.clientHeight,
        1,
        1000
      );
      camera.position.set(0, 0, 350);
      context.value.camera = camera;

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(threeContainer.value.clientWidth, threeContainer.value.clientHeight);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.0;
      threeContainer.value.appendChild(renderer.domElement);
      context.value.renderer = renderer;

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(1, 1, 1).normalize();
      scene.add(light);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.minDistance = 10;
      controls.maxDistance = 400;
      
      const objLoader = new OBJLoader();
      objLoader.load(url, (object) => {
        const textureLoader = new TextureLoader();

        textureLoader.load(
          textureURL,
          (texture) => {
            texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            texture.magFilter = THREE.LinearFilter;
            texture.minFilter = THREE.LinearMipmapLinearFilter;
            texture.encoding = THREE.sRGBEncoding;

            object.traverse((child) => {
              if (child.isMesh) {
                const originalColor = new THREE.Color(0xffffff); 
                let hsl = { h: 0, s: 0, l: 0 };
                originalColor.getHSL(hsl);
                hsl.s = Math.min(1, hsl.s * 1.5);  
                const saturatedColor = new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l);
                child.material.color = saturatedColor;
                child.material.map = texture;
                child.material.emissive = new THREE.Color(0x202020);
                child.material.needsUpdate = true;
              }
            });
            scene.add(object);

            if (props.points.length) {
              add3DPoints(props.points, scene);
            }

            animate();
            captureSnapshots();  
          },
          undefined,
          (err) => {
            console.error('Error loading texture:', err);
            alert("error");
          }
        );
      });

      const animate = () => {
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };

      animate();
      const captureSnapshots = () => {
        const originalFOV = camera.fov;  

        console.log(" Props::::::",props);
      
        if (!props.viewRightEye || !props.viewRightEye.cameraCoords || !props.viewRightEye.focalPoint) {
          console.error("viewRightEye is not properly defined or missing data");
          return;
        }
        
        if (!props.viewLeftEye || !props.viewLeftEye.cameraCoords || !props.viewLeftEye.focalPoint) {
          console.error("viewLeftEye is not properly defined or missing data");
          return;
        }
        
        const views = [
          { x: 150, y: 50, z: 150, fov: originalFOV },  
          { x: 0, y: 0, z: 350, fov: originalFOV },  
          { x: -150, y: 50, z: 150, fov: originalFOV }, 
          { 
            x: props.viewRightEye.cameraCoords[0], 
            y: props.viewRightEye.cameraCoords[1], 
            z: props.viewRightEye.cameraCoords[2], 
            fov: 27 
          }, 
          { 
            x: props.viewLeftEye.cameraCoords[0], 
            y: props.viewLeftEye.cameraCoords[1], 
            z: props.viewLeftEye.cameraCoords[2], 
            fov: 26 
          } 
        ];

        const lookAts = [
          { x: 0, y: 0, z: 0 },  
          { x: 0, y: 0, z: 0 },  
          { x: 0, y: 0, z: 0 },
          { 
            x: props.viewRightEye.focalPoint[0], 
            y: props.viewRightEye.focalPoint[1], 
            z: props.viewRightEye.focalPoint[2] 
          }, 
          { 
            x: props.viewLeftEye.focalPoint[0], 
            y: props.viewLeftEye.focalPoint[1], 
            z: props.viewLeftEye.focalPoint[2] 
          }  
        ];

        snapshots.value = [];

        views.forEach((view, index) => {
          camera.position.set(view.x, view.y, view.z);
          camera.lookAt(lookAts[index].x, lookAts[index].y, lookAts[index].z);

          camera.fov = view.fov;
          camera.updateProjectionMatrix();  

          renderer.render(scene, camera);

          const snapshot = renderer.domElement.toDataURL('image/JPEG');
          snapshots.value.push(snapshot);
        });

        emit('snapshotsCaptured', snapshots.value);

        camera.fov = originalFOV;
        camera.updateProjectionMatrix();
        camera.position.set(0, 0, 350);
        camera.lookAt(scene.position);
      };
    };

    const add3DPoints = (points, scene) => {
      if (!Array.isArray(points)) {
        console.warn("Expected points to be an array, but received:", points);
        return;
      }

      // Remove existing points
      pointObjects.value.forEach((point) => {
        scene.remove(point);
        point.geometry.dispose();
        point.material.dispose();
      });
      pointObjects.value = [];

      // Add new points
      points.forEach((point) => {
        if (Array.isArray(point) && point.length === 3) {
          const [x, y, z] = point;
          const sphereGeometry = new THREE.SphereGeometry(0.8, 32, 32);
          const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
          const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
          sphere.position.set(x, y, z);
          scene.add(sphere);
          pointObjects.value.push(sphere);
        } else {
          console.warn("Invalid point format, expected [x, y, z]:", point);
        }
      });
    };

    const updateLandmarks = (newPoints) => {
      console.log("Updating landmarks with new points:", newPoints);
      if (context.value && context.value.scene) {
        add3DPoints(newPoints, context.value.scene);
      }
    };

    onMounted(() => {
      if (!context.value) {
        context.value = {
          renderer: null,
          scene: null,
        };
        if (props.meshFileURL && props.textureFileURL) {
          loadOBJ(props.meshFileURL, props.textureFileURL);
        }
      }
    });

    onBeforeUnmount(() => {
      if (context.value && context.value.renderer) {
        context.value.renderer.dispose();
      }
    });

    watch(
      () => props.points,
      (newPoints) => {
        console.log('Updating 3D points with new data:', newPoints);
        updateLandmarks(newPoints);
      },
      { deep: true }
    );

    return {
      threeContainer,
      snapshots,
      updateLandmarks,
    };
  },
};

</script>

<style scoped>
.snapshots img {
  margin: 0 10px 0 10px;
  width: 270px;
  height: 270px;
  object-fit: contain;
}
.snapshots {
  display: flex;
  justify-content: center; 
  flex-wrap: wrap;
}
</style>